import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import SanitizedHTML from 'components/ui/sanitized_html';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp } from '@fortawesome/pro-solid-svg-icons';

import Attachments from 'bundles/apex/activities/attachments';
import LoadingSpinner from 'components/ui/loading_spinner';
import TaggedBody from 'bundles/apex/deals/tagged_body';
import useResizeObserver from 'hooks/use_resize_observer';
import { get } from 'helpers/api';
import { Button } from 'components/ui/button';

export default function ActivityBody({ activity }) {
  const [loadedBodyParts, setLoadedBodyParts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [truncated, setTruncated] = useState(true);
  const [hasOverflow, setHasOverflow] = useState();
  const bodyRef = useResizeObserver(ref => setHasOverflow(ref.current.scrollHeight - ref.current.clientHeight > 1));

  useEffect(() => {
    const element = bodyRef.current;
    if (element) {
      setHasOverflow(element.scrollHeight - element.clientHeight > 1);
      setTruncated(true);
    }
  }, [activity]);

  async function showMore() {
    if (activity.hasCrmEmail && !loadedBodyParts) {
      setLoading(true);
      setLoadedBodyParts((await get(activity.path)).bodyParts);
      setLoading(false);
    }

    setTruncated(false);
  }

  function showLess() {
    setTruncated(true);
  }

  let body;
  if (activity.hasCrmEmail && loadedBodyParts && !truncated) {
    // Activities created from an email will only have one body part. However, those that were
    // migrated from premium marketing tasks with an email body may have tags preceeding the HTML.
    // These must be split up and rendered separately. Note that email activities can't be edited,
    // so there is no risk of tags being added after the HTML.
    body = <React.Fragment>
      <TaggedBody bodyParts={loadedBodyParts.slice(0, loadedBodyParts.length - 1)} />
      <SanitizedHTML className="fade-lock" html={loadedBodyParts[loadedBodyParts.length - 1]} />
    </React.Fragment>;
  } else {
    body = <TaggedBody bodyParts={activity.bodyParts} />;
  }

  const toggle = truncated ? showMore : showLess;
  const canToggle = activity.hasCrmEmail || hasOverflow || !truncated;

  return (
    <div className="d-flex">
      <div className="flex-grow-1 me-5">
        <div className={classNames('activity-body', { truncated, 'mb-2': canToggle })} ref={bodyRef}>
          {body}
        </div>

        {canToggle &&
          <Button aria-label="toggle" variant="ghost" onClick={loading ? null : toggle}>
            {loading
              ? <LoadingSpinner inline size="sm" skipTopBuffer />
              : <FontAwesomeIcon icon={truncated ? faAngleDown : faAngleUp} />
            }
            <span className="me-2">
              {truncated ? 'Expand' : 'Collapse'}
            </span>
          </Button>
        }

        <Attachments attachments={activity.attachments} />
      </div>
    </div>
  );
}

ActivityBody.propTypes = {
  activity: PropTypes.shape({
    attachments: PropTypes.array,
    bodyParts: PropTypes.array.isRequired,
    hasCrmEmail: PropTypes.bool,
    path: PropTypes.string.isRequired,
  }).isRequired,
};
